import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useBlocker, type Location } from 'react-router-dom';
import { AppConfirmModal } from '@/components/admin/AppConfirmModal';

export type UnsavedActions = {
  /**
   * Persiste los cambios pendientes.
   *
   * CONTRATO: puede devolver una promesa. Si la devuelve, RECHAZA cuando el guardado
   * falla (la página ya mostró su propio toast de error). Esto permite ENCADENAR una
   * acción al guardado — p. ej. "Confirmar venta" guarda primero y solo avanza de etapa
   * si el guardado salió bien — en vez de avanzar a ciegas y perder los cambios.
   */
  onSave: () => void | Promise<void>;
  onDiscard: () => void;
};

type UnsavedChangesContextValue = {
  /** Cuando la página tiene cambios sin guardar, llama con { onSave, onDiscard }. Para ocultar la barra, llama con null. */
  setUnsaved: (actions: UnsavedActions | null) => void;
  unsaved: UnsavedActions | null;
};

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

export function UnsavedChangesProvider({ children }: { children: React.ReactNode }) {
  const [unsaved, setUnsavedState] = useState<UnsavedActions | null>(null);
  const setUnsaved = useCallback((actions: UnsavedActions | null) => {
    setUnsavedState(actions);
  }, []);

  /**
   * 🚨 CERRAR LA PESTAÑA CON CAMBIOS SIN GUARDAR NO PUEDE SER SILENCIOSO.
   *
   * La barra de "Cambios no guardados" avisa, pero avisar no es proteger: nada impedía cerrar la
   * pestaña, recargar o apretar atrás. Media hora cargando una ficha de producto se perdía sin una
   * sola pregunta — y el vendedor no se entera de que la perdió hasta que vuelve a entrar.
   *
   * `beforeunload` es el único gancho que el navegador da para eso. No se puede personalizar el
   * texto (los navegadores lo ignoran desde hace años, para que nadie escriba mensajes engañosos):
   * sale el cartel estándar, que es exactamente lo que hace falta.
   *
   * Se registra SOLO mientras hay cambios pendientes. Dejarlo siempre puesto haría que el navegador
   * pregunte al salir de cualquier pantalla, y una pregunta que aparece siempre se aprende a
   * ignorar — que es la forma más segura de que no proteja nada.
   */
  useEffect(() => {
    if (!unsaved) return;
    const avisar = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Firefox todavía mira el returnValue; el resto solo necesita el preventDefault.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [unsaved]);

  /**
   * 🚨 Y LA OTRA MITAD: irse a otra pantalla DENTRO de la app.
   *
   * `beforeunload` solo cubre cerrar la pestaña o recargar. Un clic en el menú lateral desmonta la
   * página, el `cleanup` limpia la barra y los cambios se van igual de callados — que es el camino
   * MÁS frecuente, porque nadie cierra el navegador para ir a Pedidos.
   *
   * `useBlocker` frena esa navegación y devuelve la decisión al vendedor. Se bloquea solo cuando la
   * ruta CAMBIA de verdad: sin esa comparación, cualquier cambio de query string —ordenar, filtrar,
   * paginar— dispararía el cartel.
   */
  const bloqueo = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) =>
        !!unsaved && currentLocation.pathname !== nextLocation.pathname,
      [unsaved],
    ),
  );

  return (
    <UnsavedChangesContext.Provider value={{ setUnsaved, unsaved }}>
      {children}
      <AppConfirmModal
        open={bloqueo.state === 'blocked'}
        onClose={() => bloqueo.reset?.()}
        title="Tenés cambios sin guardar"
        message="Si salís ahora se pierden."
        description="Podés guardarlos y volver a intentar, o salir y descartarlos."
        confirmLabel="Salir sin guardar"
        cancelLabel="Seguir editando"
        destructive
        onConfirm={() => bloqueo.proceed?.()}
      />
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges(): UnsavedChangesContextValue {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) {
    return {
      setUnsaved: () => {},
      unsaved: null,
    };
  }
  return ctx;
}
